import { AlertColor } from "@mui/material";
import {
  GenetecAlarmActionStatus,
  GenetecAlarmOutcome,
} from "./genetecAlarmSetupApi";

export interface GenetecAlarmSummary {
  severity: AlertColor;
  headline: string;
  failedAlarms: GenetecAlarmOutcome[];
  needsRecipients: string[];
}

export const TIMED_OUT_MESSAGE =
  "The alarm setup job hasn't reported back yet. It may still be running — check Config Tool → Alarms in a few minutes, or run this again.";

// Per-alarm statuses that mean the alarm was not set up. Anything else
// (created, already existed, updated) counts as ready.
const FAILED_ALARM_STATUSES = new Set(["failed", "error"]);

export const isFailedAlarm = (alarm: GenetecAlarmOutcome) =>
  FAILED_ALARM_STATUSES.has(String(alarm.status).toLowerCase());

export const recipientsSentence = (names: string[]) =>
  names.length > 0
    ? `Add recipients in Config Tool to: ${names.join(", ")}.`
    : "";

/** Turns a finished action_status row (or a poll timeout) into what the dialog shows. */
export const summarizeGenetecAlarmSetup = (
  outcome: GenetecAlarmActionStatus | "timeout"
): GenetecAlarmSummary => {
  if (outcome === "timeout" || outcome.status === "timed_out") {
    return {
      severity: "info",
      headline: TIMED_OUT_MESSAGE,
      failedAlarms: [],
      needsRecipients: [],
    };
  }
  const alarms = outcome.result?.alarms ?? [];
  const failedAlarms = alarms.filter(isFailedAlarm);
  const needsRecipients = outcome.result?.needs_recipients ?? [];

  switch (outcome.status) {
    case "success":
      return {
        severity: "success",
        headline: ["Alarms ready.", recipientsSentence(needsRecipients)]
          .filter(Boolean)
          .join(" "),
        failedAlarms: [],
        needsRecipients,
      };
    case "partial":
      return {
        severity: "warning",
        headline: "Some alarms were set up, but these failed:",
        failedAlarms,
        needsRecipients,
      };
    default: {
      const reason = outcome.result?.error;
      return {
        severity: "error",
        headline:
          failedAlarms.length > 0
            ? "Alarm setup failed for:"
            : `Alarm setup failed${reason ? `: ${reason}` : "."}`,
        failedAlarms,
        needsRecipients: [],
      };
    }
  }
};
